import { defineStore } from 'pinia';
import useCommonStore from '@/common/store/pinia/common';
import { ref, computed } from 'vue';
import api from '@/common/utils/api';
import AwsS3Client from '@/common/utils/s3Client';
import { EXPIRES_URL_TIME } from '@/common/constants/s3.constants';
import uniqid from 'uniqid';
import i18n from '@/common/i18n';
import { NotificationType } from '@/common/constants/notifications.constants';
import { FileUploadStatuses, FILE_UPLOAD_STATUS_MAP } from '@/common/constants/fileUpload.constants';
import {
  PutObjectCommand,
  GetObjectCommand,
  ListObjectsV2Command,
  DeleteObjectsCommand
} from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import store from '@/common/store';
import { isEqual } from 'lodash-es';
import { decode } from 'html-entities';
import CancellationError from '@/common/utils/errors/CancellationError';
import { DEFAULT_REQUEST_VIEW } from '@/common/store/modules/common/common.constants';
import ValidationError from '@/common/utils/errors/ValidationError';

interface ParamsFilters {
  query?: string;
}

interface Params {
  filters?: ParamsFilters;
  limit?: number;
}

interface FetchOptions {
  view?: string;
  bucketName: string;
  prefix?: string;
  params?: Params;
}

export interface S3FileState {
  status: string,
  id: ID,
  bucketName: string,
  file: File | null,
  progress: number,
  request: XMLHttpRequest | null,
  fullPath: string,
  isFolder: boolean,
  type: string,
  name: string,
  folderPath: string,
}

interface S3Bucket {
  id?: string | Date;
  path?: string[];
}

interface S3BucketFile {
  Key?: string;
  ETag?: string;
  Prefix?: string;
  Size?: number;
  StorageClass?: string;
  LastModified?: Date;
}

export const mapper = (item: S3BucketFile) => {
  const prefix = decode(item.Prefix) || '';
  const isFolder = !!prefix;
  const key = decode(item.Key) || '';
  const path = isFolder && prefix ? prefix : key;
  const name = path.split('/').filter(Boolean).pop() || '';
  const etag = !isFolder && item.ETag ? item.ETag.replace(/"/g, '') : null;
  const id = isFolder ? `folder-${name}` : name;
  let type;
  if (!isFolder) {
    const extension = key?.split('.').pop();
    type = extension ? extension.toLowerCase() : '';
  } else {
    type = '';
  }

  return {
    ...item,
    id,
    size: item.Size,
    lastModified: item.LastModified,
    Key: id,
    type,
    prefix,
    isFolder,
    name,
    key,
    etag,
  };
};

export type S3BucketFileModel = ReturnType<typeof mapper>;

let uploadingOperationId: ID | null = null;
const s3Client = AwsS3Client.getInstance();

export const useS3FilesStore = defineStore('s3Files', () => {
  const commonStore = useCommonStore<S3BucketFile, S3BucketFileModel>({
    getUrl: () => '',
    plural: (data) => data,
    mapItem: mapper,
    itemKey: 'Key',
    pagination: 's3'
  })();

  const {
    viewLists,
    pushData,
    getItemById,
    setData,
    removeItem
  } = commonStore;

  const uploadingFiles = ref([] as S3FileState[]);
  const buckets = ref([] as S3Bucket[]);
  const requestParams = ref<Params>({});
  const viewName = ref(DEFAULT_REQUEST_VIEW);
  const pageLimit = 36;

  const getPendingFile = computed(() => uploadingFiles.value.find(f => f.status === FileUploadStatuses.PENDING));

  const getFileFromEntry = (fileEntry: FileSystemFileEntry): Promise<File> => new Promise((resolve, reject) => {
    fileEntry.file(resolve, reject);
  });

  const setUploadingEntryFiles = async (bucketName: string, files: FileSystemEntry[], filePath: string) => {
    const newFiles = await Promise.all(Object.values(files).map(async (entry: FileSystemEntry) => {
      const fullPath = entry.fullPath.replace('/', '');
      const lastIndex = fullPath.lastIndexOf(`/${entry.name}`);
      const file = entry.isFile ? await getFileFromEntry(entry as FileSystemFileEntry) : null;

      const path = fullPath.substring(0, lastIndex);
      let folderPath;
      if (filePath && path) {
        folderPath = `${filePath}/${path}`;
      } else if (filePath) {
        folderPath = filePath;
      } else {
        folderPath = path;
      }

      return {
        status: FileUploadStatuses.PENDING,
        statusText: FILE_UPLOAD_STATUS_MAP[FileUploadStatuses.PENDING].text,
        statusIcon: FILE_UPLOAD_STATUS_MAP[FileUploadStatuses.PENDING].icon,
        id: uniqid(entry.name),
        bucketName,
        file,
        progress: 0,
        request: null,
        fullPath: filePath ? `${filePath}/${fullPath}` : fullPath,
        folderPath,
        type: file?.type || '',
        name: entry.name.replace('/', ''),
        isFolder: entry.isDirectory
      };
    }));
    uploadingFiles.value.push(...newFiles);
  };

  const setUploadingFiles = async (bucketName: string, files: File[], filePath: string) => {
    const newFiles = await Promise.all(Object.values(files).map(async (file: File) => ({
      status: FileUploadStatuses.PENDING,
      statusText: FILE_UPLOAD_STATUS_MAP[FileUploadStatuses.PENDING].text,
      statusIcon: FILE_UPLOAD_STATUS_MAP[FileUploadStatuses.PENDING].icon,
      id: uniqid(file.name),
      bucketName,
      file,
      progress: 0,
      request: null,
      fullPath: filePath ? `${filePath}/${file.name}` : file.name,
      folderPath: filePath,
      type: file.type || 'any',
      name: file.name,
      isFolder: false,
    })));
    uploadingFiles.value.push(...newFiles);
  };

  const updateUploadingFile = (file) => {
    uploadingFiles.value = uploadingFiles.value.map((f) => {
      if (file.id === f.id) {
        return {
          ...f,
          ...file,
          statusText: FILE_UPLOAD_STATUS_MAP[file.status].text,
          statusIcon: FILE_UPLOAD_STATUS_MAP[file.status].icon
        };
      }
      return f;
    });
  };

  const fetchObjects = ({ bucketName, prefix = '', delimiter, maxKeys = 1000, continuationToken = '' }) => {
    const command = new ListObjectsV2Command({
      Bucket: bucketName,
      Delimiter: delimiter ? '/' : '',
      Prefix: prefix,
      MaxKeys: maxKeys,
      ContinuationToken: continuationToken
    });
    return s3Client.send(command);
  };

  const fetchItems = ({ view = DEFAULT_REQUEST_VIEW, bucketName, prefix, params = {} }: FetchOptions) => {
    viewLists.value[view].loading = true;
    viewName.value = view;

    if (!isEqual(requestParams.value, params)) {
      requestParams.value = {
        filters: params?.filters,
        limit: params?.limit || pageLimit
      };
      viewLists.value[view].list = [];
      viewLists.value[view].count = 0;
      viewLists.value[view].marker = undefined;
      viewLists.value[view].isLastPage = false;
    }

    let prefixQuery;
    const query = requestParams.value.filters?.query;
    if (prefix) {
      prefixQuery = query ? `${prefix}/${query}` : `${prefix}/`;
    } else {
      prefixQuery = query ? `${query}` : '';
    }

    const { limit } = requestParams.value;

    return fetchObjects({
      bucketName,
      prefix: prefixQuery,
      delimiter: true,
      maxKeys: limit,
      continuationToken: viewLists.value[view].marker
    }).then(data => {
      const { CommonPrefixes = [], Contents = [], NextContinuationToken, IsTruncated } = data;
      viewLists.value[view].isLastPage = !IsTruncated;

      const commonPrefixes = CommonPrefixes.reduce((acc: S3BucketFile[], folder) => {
        const newCur = mapper(folder);
        if (prefix && newCur.prefix === `${prefix}/`) {
          return acc;
        }
        const folderPrefix = decode(folder.Prefix);
        acc.push({ Key: folderPrefix, Prefix: folderPrefix } as S3BucketFile);
        return acc;
      }, []);
      const contents = Contents.reduce((acc: S3BucketFile[], file) => {
        const newCur = mapper(file);
        if (prefix && newCur.key === `${prefix}/`) {
          return acc;
        }
        acc.push({ ...file, Key: decode(file.Key) } as S3BucketFile);
        return acc;
      }, []);
      const contentData = commonPrefixes.concat(contents);
      if (viewLists.value[view].marker) {
        pushData(contentData, view);
      } else {
        setData(contentData, view);
        viewLists.value[view].count = contentData.length;
      }
      viewLists.value[view].loading = false;
      viewLists.value[view].marker = NextContinuationToken;
    }).catch((err) => {
      viewLists.value[view].loading = false;
      viewLists.value[view].error = err;
      viewLists.value[view].list = [];
      throw err;
    });
  };

  const uploadFolder = (bucketName, path, name) => {
    const folderPath = path ? `${path}/${name}/` : `${name}/`;
    const command = new PutObjectCommand({ Bucket: bucketName, Key: folderPath });
    return s3Client.send(command).then(() => {
      const view = `${bucketName}-${path}`;
      pushData([{ Key: folderPath, Prefix: folderPath }], view);
    });
  };

  const updateUploadingNotification = (view = 'default') => {
    const currentView = viewLists.value[view];
    const filesCount = uploadingFiles.value.length;
    const loadedFiles = uploadingFiles.value.filter(f => f.status === FileUploadStatuses.UPLOADED).length;
    const pendingFiles = uploadingFiles.value.filter(f => f.status === FileUploadStatuses.PENDING || f.status === FileUploadStatuses.PROGRESS).length;
    const failedFiles = uploadingFiles.value.filter(f => f.status === FileUploadStatuses.FAILED).length;
    if (pendingFiles) {
      store.dispatch('notifications/UPDATE', {
        id: uploadingOperationId,
        type: NotificationType.PROGRESS,
        message: i18n.t('Uploading files: {loadedFiles} of {filesCount}', { loadedFiles, filesCount })
      }, { root: true });
    } else if (failedFiles === filesCount) {
      const isValidationError = currentView.error instanceof ValidationError && currentView.error.name === 'AccessDenied';
      const message = isValidationError ? i18n.t('Uploading files is restricted by policy') : i18n.t('Failed to upload files to bucket "{0}"', [uploadingFiles.value[0].bucketName]);

      store.dispatch('notifications/UPDATE', {
        id: uploadingOperationId,
        type: NotificationType.ERROR,
        title: i18n.t('Error uploading files'),
        message,
        preventReload: false,
        actions: [],
      }, { root: true });
      uploadingOperationId = null;
    } else {
      store.dispatch('notifications/UPDATE', {
        id: uploadingOperationId,
        type: NotificationType.SUCCESS,
        title: i18n.t('Files uploaded'),
        message: i18n.t('{loadedFiles} of {filesCount} files successfully uploaded to buckets', {
          loadedFiles,
          filesCount
        }),
        preventReload: false,
        actions: [],
        showBottom: true
      }, { root: true });
      uploadingOperationId = null;
    }
  };

  const uploadFile = async (item) => {
    const options = {
      method: 'put',
    };
    const onProgress = (progress, xhr) => {
      updateUploadingFile({
        id: item.id,
        status: FileUploadStatuses.PROGRESS,
        progress: progress.loaded / progress.total * 100,
        ...(!item.request ? { request: xhr } : {})
      });
    };
    let promise;
    if (item.isFolder) {
      promise = uploadFolder(item.bucketName, item.folderPath, item.name);
    } else {
      const command = new PutObjectCommand({ Bucket: item.bucketName, Key: item.fullPath });
      const url = await getSignedUrl(s3Client, command, { expiresIn: EXPIRES_URL_TIME });
      promise = api.upload(url, item.file, options, onProgress.bind(this));
    }

    const view = `${item.bucketName}-${item.folderPath}`;

    if (viewLists.value[view]?.error?.name === 'AccessDenied') {
      return promise.then(() => {
        updateUploadingFile({
          id: item.id,
          status: FileUploadStatuses.UPLOADED,
          request: null
        });
      }).catch(() => {
        updateUploadingFile({
          id: item.id,
          status: FileUploadStatuses.FAILED,
        });
      }).finally(() => {
        updateUploadingNotification(view);
      });
    }

    return promise.then(async () => {
      updateUploadingFile({
        id: item.id,
        status: FileUploadStatuses.UPLOADED,
        request: null
      });

      const { Contents = [], CommonPrefixes = [] } = await fetchObjects({ bucketName: item.bucketName, prefix: item.fullPath, delimiter: true, maxKeys: 1 });
      const newData = Contents || CommonPrefixes.map(prefixItem => ({ ...prefixItem, Key: decode(prefixItem.Prefix) }));

      pushData(newData as S3BucketFile[], view);
    })
      .catch((err) => {
        if (!(err instanceof CancellationError)) {
          viewLists.value[view].error = err;
          updateUploadingFile({
            id: item.id,
            status: FileUploadStatuses.FAILED,
          });
        }
      })
      .finally(() => {
        const nextItem = getPendingFile.value;
        if (nextItem) {
          updateUploadingFile({
            id: nextItem.id,
            status: FileUploadStatuses.PROGRESS,
          });
          uploadFile(nextItem);
        }
        updateUploadingNotification(view);
      });
  };

  const uploadFiles = async (bucketName, files, filePath) => {
    if (!uploadingOperationId) {
      uploadingFiles.value = [];
    }
    if (files[0] instanceof File) {
      await setUploadingFiles(bucketName, files, filePath);
    } else {
      await setUploadingEntryFiles(bucketName, files, filePath);
    }
    if (uploadingOperationId) {
      updateUploadingNotification();
      return null;
    }
    uploadingOperationId = uniqid();

    store.dispatch('notifications/ADD', {
      id: uploadingOperationId,
      type: NotificationType.PROGRESS,
      preventReload: true,
      skipStore: true,
      message: i18n.t('Uploading files: {loaded} of {count}', { loaded: 0, count: uploadingFiles.value.length }),
      actions: [{
        label: i18n.t('Show'),
        name: 'openFileLoadingDialog',
      },
      {
        label: i18n.t('Cancel all'),
        name: 'cancelUploads',
      }]
    }, { root: true });

    return uploadFile(uploadingFiles.value[0]);
  };

  const abortUpload = (file) => {
    if (file.request && typeof file.request.abort === 'function') {
      file.request.abort();
    }
    if (file.status === FileUploadStatuses.PENDING || file.status === FileUploadStatuses.PROGRESS) {
      updateUploadingFile({ id: file.id, status: FileUploadStatuses.CANCELED });
    }
    updateUploadingNotification();
  };

  const abortUploads = () => {
    uploadingFiles.value.forEach((file) => {
      abortUpload(file);
    });
  };

  const deleteObjects = async (bucketName, idList, path) => {
    const objects = [] as any[];

    const promises = idList.map(async id => {
      const item = getItemById.value(id);
      if (!item) {
        return;
      }
      if (!item.isFolder) {
        objects.push({ Key: item.key });
      } else {
        const prefix = path ? `${path}/${item.name}/` : `${item.name}/`;
        const { Contents = [] } = await fetchObjects({ bucketName, prefix, delimiter: false });
        Contents.forEach(file => {
          objects.push({ Key: decode(file.Key) });
        });
      }
    });
    await Promise.all(promises);
    const command = new DeleteObjectsCommand({
      Bucket: bucketName,
      Delete: {
        Objects: objects
      }
    });
    return s3Client.send(command).then((response) => {
      const filesToDelete = idList.filter(id => !response.Errors?.some(error => error.Key === id));
      filesToDelete.forEach(id => removeItem(id));
      return response;
    });
  };

  const getObjectUrl = (bucketName, key) => {
    const command = new GetObjectCommand({ Bucket: bucketName, Key: key });
    return getSignedUrl(s3Client, command, { expiresIn: EXPIRES_URL_TIME });
  };

  return {
    ...commonStore,
    uploadingFiles,
    buckets,
    getPendingFile,
    requestParams,
    setUploadingFiles,
    setUploadingEntryFiles,
    updateUploadingFile,
    uploadFolder,
    uploadFile,
    uploadFiles,
    updateUploadingNotification,
    abortUpload,
    abortUploads,
    fetchItems,
    deleteObjects,
    getObjectUrl,
    fetchObjects
  };
});

export default useS3FilesStore;
